import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { api } from 'utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const KEYS = {
  token: 'edswot_token',
  user: 'edswot_user',
  profiles: 'edswot_profiles',
  activeProfile: 'edswot_active_profile',
  subscription: 'edswot_subscription',
};

const getEndsAt = (sub) => sub?.subscription_ends_at || sub?.current_period_end || null;

const fromLocal = (key) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(KEYS.token));
  const [user, setUserState] = useState(() => fromLocal(KEYS.user));
  const [profiles, setProfilesState] = useState(() => fromLocal(KEYS.profiles) || []);
  const [activeProfile, setActiveProfileState] = useState(() => fromLocal(KEYS.activeProfile));
  const [subscription, setSubscriptionState] = useState(() => fromLocal(KEYS.subscription));

  // Auto-fetch subscription whenever the user logs in (token changes)
  useEffect(() => {
    if (!token) return;
    api.get('/payment/subscription')
      .then((data) => {
        const sub = data.subscription || null;
        if (sub) {
          localStorage.setItem(KEYS.subscription, JSON.stringify(sub));
          setSubscriptionState(sub);
        }
      })
      .catch(() => {}); // silently fail — subscription may not exist yet
  }, [token]);

  const refreshProfiles = useCallback(() => {
    if (!localStorage.getItem(KEYS.token)) return;
    api.get('/profiles')
      .then((data) => {
        const profs = data.profiles || data.data || [];
        if (!Array.isArray(profs)) return;
        localStorage.setItem(KEYS.profiles, JSON.stringify(profs));
        setProfilesState(profs);
        // If no active profile is set yet, default to the first/default one
        setActiveProfileState((prev) => {
          if (prev) return prev;
          const def = profs.find((p) => p.is_default) || profs[0] || null;
          if (def) localStorage.setItem(KEYS.activeProfile, JSON.stringify(def));
          return def;
        });
      })
      .catch(() => {});
  }, []);

  const setSubscription = useCallback((sub) => {
    localStorage.setItem(KEYS.subscription, JSON.stringify(sub));
    setSubscriptionState(sub);
  }, []);

  // null = not yet loaded (unknown), true = active, false = inactive/expired
  const isSubscriptionActive = useMemo(() => {
    if (!subscription) return null;
    if (subscription.status !== 'active') return false;
    const endsAt = getEndsAt(subscription);
    if (!endsAt) return true;
    return new Date(endsAt) > new Date();
  }, [subscription]);

  const subscriptionDaysLeft = useMemo(() => {
    const endsAt = getEndsAt(subscription);
    if (!endsAt) return null;
    return Math.ceil((new Date(endsAt) - new Date()) / (1000 * 60 * 60 * 24));
  }, [subscription]);

  const login = useCallback((data) => {
    const { token: t, user: u } = data;
    const profs = u.profiles || [];
    const defaultProfile = profs.find((p) => p.is_default) || profs[0] || null;

    localStorage.setItem(KEYS.token, t);
    localStorage.setItem(KEYS.user, JSON.stringify(u));
    localStorage.setItem(KEYS.profiles, JSON.stringify(profs));
    if (defaultProfile) localStorage.setItem(KEYS.activeProfile, JSON.stringify(defaultProfile));
    if (u.role) localStorage.setItem('userRole', u.role);

    setToken(t);
    setUserState(u);
    setProfilesState(profs);
    setActiveProfileState(defaultProfile);
  }, []);

  const logout = useCallback(() => {
    Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem('userRole');
    setToken(null);
    setUserState(null);
    setProfilesState([]);
    setActiveProfileState(null);
    setSubscriptionState(null);
  }, []);

  const setProfiles = useCallback((profs) => {
    localStorage.setItem(KEYS.profiles, JSON.stringify(profs));
    setProfilesState(profs);
  }, []);

  const setActiveProfile = useCallback((profile) => {
    localStorage.setItem(KEYS.activeProfile, JSON.stringify(profile));
    setActiveProfileState(profile);
  }, []);

  const updateUser = useCallback(
    (updated) => {
      const merged = { ...user, ...updated };
      localStorage.setItem(KEYS.user, JSON.stringify(merged));
      setUserState(merged);
    },
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        profiles,
        activeProfile,
        subscription,
        isAuthenticated: !!token,
        isSubscriptionActive,
        subscriptionDaysLeft,
        login,
        logout,
        setProfiles,
        setActiveProfile,
        refreshProfiles,
        updateUser,
        setSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
